// Connector helpers for the dashboard pages. No React and no api client at
// runtime.

import type { QueryClient } from "@tanstack/react-query";
import { highlightLines, langOf, type Token } from "./highlight.ts";
import type { CodeHostKind, ConnectorActor, ConnectorMovedBy, ConnectorRisk, ConnectorSourceKind, ConnectorStatus, ConnectorSummary, ToolState } from "./api.ts";

export const RISK_LABEL: Record<ConnectorRisk, string> = {
  read: "Reads",
  write: "Changes data",
  destructive: "Deletes or can't be undone",
};

export const STATE_LABEL: Record<ToolState, string> = {
  on: "On",
  ask: "Ask first",
  off: "Off",
};

export const STATE_HINT: Record<ToolState, string> = {
  on: "Boo runs it when needed.",
  ask: "Boo pauses until a person approves that exact run.",
  off: "Boo can't run it.",
};

// A version is registered and waits on a person to approve or merge it.
export function awaitsReview(c: Pick<ConnectorSummary, "pendingCommit">): boolean {
  return !!c.pendingCommit;
}

// Mirrors the backend default for an action already in the running version.
export function defaultStateFor(risk: ConnectorRisk): ToolState {
  return risk === "read" ? "on" : risk === "write" ? "ask" : "off";
}

export const STATUS_LABEL: Record<ConnectorStatus, string> = {
  draft: "Not ready",
  in_review: "Waiting for approval",
  live: "Running",
  disabled: "Disconnected",
};

// "Boo", a person's name, who approved in chat, or "hugo on GitLab". `source`
// names the host when the row doesn't.
export function actorName(actor: ConnectorActor, source?: ConnectorSourceKind): string {
  switch (actor.type) {
    case "boo":
      return "Boo";
    case "user":
      return actor.name || "Someone";
    case "person":
      return actor.displayName || "Someone";
    case "link":
      return actor.sentTo ? `via the link Boo sent to ${actor.sentTo}` : "via a link Boo sent";
    case "host": {
      const host = actor.host ?? (source === "github" || source === "gitlab" ? source : null);
      const who = actor.login || "Someone";
      return host ? `${who} on ${host === "github" ? "GitHub" : "GitLab"}` : who;
    }
    default:
      return "Someone";
  }
}

export const shortCommit = (commit: string) => commit.slice(0, 7);

export const VERIFY_ACTION = "verify";

export type DiffLine = {
  kind: "same" | "add" | "del";
  text: string;
  // 1-based line numbers on each side.
  oldNo: number | null;
  newNo: number | null;
};

// Highlighted tokens for each line of a file's diff. Each side is highlighted
// whole, so strings and comments spanning lines color right. A line whose
// tokens don't spell out exactly its diff text (the highlighter splits on \r
// too, lineDiff only on \n) shows plain: the row always shows what changed.
export function diffLineTokens(path: string, before: string | null, after: string | null): (line: DiffLine) => Token[] {
  const lang = langOf(path);
  const old = before ? highlightLines(before, lang) : [];
  const now = after ? highlightLines(after, lang) : [];
  const spells = (tokens: Token[] | undefined, text: string) => !!tokens && tokens.map((t) => t.text).join("") === text;
  return (l) => {
    const fromOld = l.oldNo !== null ? old[l.oldNo - 1] : undefined;
    const fromNew = l.newNo !== null ? now[l.newNo - 1] : undefined;
    // An unchanged line is on both sides; either may agree.
    const tokens = [l.kind === "del" ? fromOld : fromNew, l.kind === "same" ? fromOld : undefined].find((t) => spells(t, l.text));
    return tokens ?? [{ text: l.text }];
  };
}

// Line diff by longest common subsequence. Connector files are small; past
// MAX_CELLS the whole file shows as removed + added.
const MAX_CELLS = 4_000_000;

export function lineDiff(before: string | null, after: string | null): DiffLine[] {
  const a = before === null || before === "" ? [] : before.replace(/\n$/, "").split("\n");
  const b = after === null || after === "" ? [] : after.replace(/\n$/, "").split("\n");
  const n = a.length;
  const m = b.length;
  const out: DiffLine[] = [];
  if (n * m > MAX_CELLS) {
    a.forEach((text, i) => out.push({ kind: "del", text, oldNo: i + 1, newNo: null }));
    b.forEach((text, j) => out.push({ kind: "add", text, oldNo: null, newNo: j + 1 }));
    return out;
  }
  // lcs[i][j] = LCS length of a[i..] and b[j..].
  const lcs: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) {
      out.push({ kind: "same", text: a[i], oldNo: i + 1, newNo: j + 1 });
      i++;
      j++;
    } else if (i < n && (j === m || lcs[i + 1][j] >= lcs[i][j + 1])) {
      // Removals before additions, as git shows them.
      out.push({ kind: "del", text: a[i], oldNo: i + 1, newNo: null });
      i++;
    } else {
      out.push({ kind: "add", text: b[j], oldNo: null, newNo: j + 1 });
      j++;
    }
  }
  return out;
}

// hiddenAfter: unchanged lines folded after the last hunk.
export type DiffHunk = { lines: DiffLine[]; hiddenBefore: number; hiddenAfter?: number };

// Changed lines with `context` unchanged lines around them; the rest folds.
export function diffHunks(lines: DiffLine[], context = 3): DiffHunk[] {
  const keep = new Array<boolean>(lines.length).fill(false);
  lines.forEach((l, idx) => {
    if (l.kind === "same") return;
    for (let k = Math.max(0, idx - context); k <= Math.min(lines.length - 1, idx + context); k++) keep[k] = true;
  });
  const hunks: DiffHunk[] = [];
  let hidden = 0;
  let current: DiffLine[] | null = null;
  lines.forEach((l, idx) => {
    if (!keep[idx]) {
      if (current) {
        hunks.push({ lines: current, hiddenBefore: hidden });
        current = null;
        hidden = 0;
      }
      hidden++;
      return;
    }
    (current ??= []).push(l);
  });
  if (current) hunks.push({ lines: current, hiddenBefore: hidden });
  else if (hidden > 0 && hunks.length > 0) hunks[hunks.length - 1].hiddenAfter = hidden;
  return hunks;
}

// Lines (0-based) of `text` that read a secret variable: `vars.api_key`,
// `vars?.api_key`, `vars["api_key"]`, or a destructuring of vars that names it
// (`const { api_key } = ctx.vars`, `{ vars: { api_key } }` in parameters), also
// when it spans lines or nests one level (`opts = {}`, `{ a: { b } }`). Not
// marked: reads through a renamed copy, two levels of nesting, a `{` or `}`
// inside a string or comment within the pattern, and `ctx["vars"]`.
export function secretLines(text: string | null | undefined, variables: { name: string; secret?: boolean }[]): Set<number> {
  const out = new Set<number>();
  const names = variables.filter((v) => v.secret).map((v) => v.name.replace(/[^A-Za-z0-9_$]/g, "")).filter(Boolean);
  if (!text || names.length === 0) return out;
  const alt = names.join("|");
  const direct = new RegExp(`\\bvars\\s*(?:\\?\\.|\\.)\\s*(?:${alt})\\b|\\bvars\\s*(?:\\?\\.\\s*)?\\[\\s*["'\`](?:${alt})["'\`]\\s*\\]`, "g");
  const body = String.raw`\{(?:[^{}]|\{[^{}]*\})*\}`;
  const destructure = new RegExp(String.raw`${body}\s*=\s*(?:[\w$]+\s*\??\.\s*)*vars\b(?!\s*\??\.)|\bvars\s*:\s*${body}`, "g");
  const name = new RegExp(`\\b(?:${alt})\\b`, "g");
  const starts = [0];
  for (let i = 0; i < text.length; i++) if (text[i] === "\n") starts.push(i + 1);
  const lineOf = (pos: number) => {
    let lo = 0;
    let hi = starts.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (starts[mid] <= pos) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  };
  for (const m of text.matchAll(direct)) out.add(lineOf(m.index));
  for (const m of text.matchAll(destructure)) {
    for (const n of m[0].matchAll(name)) out.add(lineOf(m.index + n.index));
  }
  return out;
}

// A changed diff line reads a secret, by the secret lines of each side.
export function changesSecret(line: DiffLine, before: Set<number>, after: Set<number>): boolean {
  if (line.kind === "del") return line.oldNo !== null && before.has(line.oldNo - 1);
  if (line.kind === "add") return line.newNo !== null && after.has(line.newNo - 1);
  return false;
}

// The platform's build checks, in plain words. Unknown names show as sent.
const BUILD_CHECK_LABEL: Record<string, string> = {
  format: "Format is valid",
  handlers: "One handler per action",
  variables: "Uses only declared variables",
  imports: "Uses only allowed imports",
  types: "Code matches the declared inputs and outputs",
  bundle: "Builds",
  tests: "Tests pass",
};

export function buildCheckLabel(name: string): string {
  return BUILD_CHECK_LABEL[name] ?? name;
}

// A call's outcome in the activity list: the backend sends "ok", a status
// such as "approval_required", or the error code the call failed with.
// Anything unlisted reads as Failed; the row's detail carries the message.
export type OutcomeTone = "ok" | "ask" | "off" | "bad";
const CALL_OUTCOME: Record<string, { label: string; tone: OutcomeTone }> = {
  ok: { label: "OK", tone: "ok" },
  ran: { label: "OK", tone: "ok" },
  approval_required: { label: "Asked first", tone: "ask" },
  asked: { label: "Asked first", tone: "ask" },
  action_off: { label: "Turned off", tone: "off" },
  denied: { label: "Refused", tone: "bad" },
  refused: { label: "Refused", tone: "bad" },
  grant_denied: { label: "Refused", tone: "bad" },
  declined: { label: "Declined", tone: "bad" },
  expired: { label: "Approval expired", tone: "off" },
  already_decided: { label: "Already decided", tone: "off" },
  not_live: { label: "Not running", tone: "off" },
  not_connected: { label: "Not connected", tone: "off" },
  not_available: { label: "Not available", tone: "off" },
  unknown_action: { label: "Unknown action", tone: "bad" },
  invalid_input: { label: "Bad input", tone: "bad" },
  invalid_request: { label: "Bad input", tone: "bad" },
  input_mismatch: { label: "Request changed", tone: "bad" },
  version_changed: { label: "Version changed", tone: "off" },
  bad_output: { label: "Bad output", tone: "bad" },
  output_too_large: { label: "Output too large", tone: "bad" },
  timeout: { label: "Timed out", tone: "bad" },
  runner_unavailable: { label: "Couldn't run", tone: "bad" },
};

export function callOutcome(code: string): { label: string; tone: OutcomeTone } {
  return CALL_OUTCOME[code] ?? { label: "Failed", tone: "bad" };
}

// The connector page's sections (?tab=).
export const CONNECTOR_TABS = ["review", "connection", "actions", "versions", "activity", "code"] as const;
export type ConnectorTab = (typeof CONNECTOR_TABS)[number];

export const connectorKeys = {
  // Every connector query of one Boo; also the list's key.
  all: (containerId: string) => ["connectors", containerId] as const,
  list: (containerId: string) => ["connectors", containerId] as const,
  detail: (containerId: string, slug: string) => ["connectors", containerId, slug] as const,
  review: (containerId: string, slug: string, commit: string) => ["connectors", containerId, slug, "review", commit] as const,
  versions: (containerId: string, slug: string) => ["connectors", containerId, slug, "versions"] as const,
  activity: (containerId: string, slug: string) => ["connectors", containerId, slug, "activity"] as const,
  files: (containerId: string, slug: string, commit: string) => ["connectors", containerId, slug, "files", commit] as const,
  versionDiff: (containerId: string, slug: string, commit: string) =>
    ["connectors", containerId, slug, "versions", commit, "diff"] as const,
  setup: (containerId: string, slug: string) => ["connector-setup", containerId, slug] as const,
  link: (token: string) => ["connector-link", token] as const,
};

// After a change: refetch the Boo's connector queries except a commit's files
// and diffs, which never change.
export function invalidateConnectors(queryClient: QueryClient, containerId: string) {
  return queryClient.invalidateQueries({
    queryKey: connectorKeys.all(containerId),
    predicate: (q) => q.queryKey[3] !== "files" && q.queryKey[5] !== "diff",
  });
}

export function monogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// "Checks the key" → "checks the key", to follow "The connection test ".
export const lowerFirst = (s: string) => (s ? s[0].toLowerCase() + s.slice(1) : s);

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// "!4" on GitLab, "#4" on GitHub.
export const requestNumber = (host: CodeHostKind, n: number) => `${host === "gitlab" ? "!" : "#"}${n}`;

export const requestNoun = (host: CodeHostKind) => (host === "github" ? "pull request" : "merge request");

// The web page of request n in the repository at repoUrl.
export const requestUrl = (repoUrl: string, host: CodeHostKind, n: number) =>
  `${repoUrl.replace(/\/$/, "")}${host === "gitlab" ? "/-/merge_requests/" : "/pull/"}${n}`;

// "merged by rita (!4)" or "pushed by root".
export function movedByText(m: ConnectorMovedBy, host: ConnectorSourceKind): string {
  if (m.via === "push") return `pushed by ${m.login}`;
  const n = m.prNumber && (host === "github" || host === "gitlab") ? ` (${requestNumber(host, m.prNumber)})` : "";
  return `merged by ${m.login}${n}`;
}
